import React, { useState } from 'react';
import {
  TextField, IconButton, Tooltip,
  SpeedDial, SpeedDialAction, Dialog
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Person3Icon from '@mui/icons-material/Person3';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ViewListIcon from '@mui/icons-material/ViewList';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { useSnackbar } from '../../SnackbarContext.js';
import SetPasswordFields from '../../DynamicIsland/SetPasswordFields.js';
import LoginHistoryTimeline from './LoginHistoryTimeline.js';
import { ModelType, LogType, LogCategory } from '../../../models';
import './UserFunctionPanel.css';

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

export default function UserFunctionPanel({
  user,
  logs,
  onResetPassword,
  onSendPasswordResetEmail,
  onDeactivate,
  onAdminise,
}) {
  const [formData, setFormData] = useState({ ...user });
  const [focusedField, setFocusedField] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [eventLogView, setEventLogView] = useState('table');
  const [activeSection, setActiveSection] = useState('login');
  const { openSnackbar } = useSnackbar();

  const runAction = (handler, label) => () => {
    if (typeof handler === 'function') {
      handler(user);
      return;
    }
    openSnackbar({ message: `${label} isn't wired up yet.`, variant: 'info' });
  };

  const handleResetPasswordClick = () => {
    if (typeof onResetPassword === 'function') {
      setShowPasswordForm(true);
      return;
    }
    openSnackbar({ message: `Reset Password isn't wired up yet.`, variant: 'info' });
  };

  // Same three role flags/icons as the Users grid's Submitter/Reviewer/Admin
  // columns (Enums.js userRole columns + FieldRenderers.js's USER_ROLE_BADGES).
  const roleBadges = [
    { key: 'submitter', label: 'Submitter', icon: Person3Icon, active: !!user?.submitter },
    { key: 'reviewer', label: 'Reviewer', icon: RateReviewIcon, active: !!user?.reviewer },
    { key: 'admin', label: 'Admin', icon: AdminPanelSettingsIcon, active: !!user?.admin },
  ].filter((badge) => badge.active);

  const summaryCards = [
    {
      key: 'submissions',
      label: 'Submissions',
      value: user.numberOfSubmissions ?? 0,
      breakdown: [
        { label: 'Submitted', value: user.numberOfCompletedSubmissions ?? 0 },
        { label: 'Incomplete', value: (user.numberOfSubmissions ?? 0) - (user.numberOfCompletedSubmissions ?? 0) },
        { label: 'Accepted', value: user.numberOfAcceptedSubmissions ?? 0 },
      ],
    },
    {
      key: 'reviews',
      label: 'Reviews Assigned',
      value: user.numberOfAssignments ?? 0,
      breakdown: [
        { label: 'Complete', value: user.numberOfCompletedAssignments ?? 0 },
        // Only meaningful once they've actually completed a review —
        // otherwise it's just a misleading 0.
        ...(user.numberOfCompletedAssignments > 0
          ? [{ label: 'Avg Score', value: (user.averageScorePerAssignment ?? 0).toFixed(1) }]
          : []),
      ],
    },
  ];

  const userLogs = Array.isArray(logs)
    ? logs
        .filter((log) =>
          log?.entityId === user?.id &&
          log?.modelType === ModelType.USER &&
          log?.type === LogType.COMPLETE
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  // Set on the sending ozlet's own Logs row, not Oz's wrapper (Oz's category
  // just mirrors whatever was last on the workflow payload, so it can't be
  // trusted for multi-stage workflows).
  const emailLogs = Array.isArray(logs)
    ? logs
        .filter((log) =>
          log?.entityId === user?.id &&
          log?.modelType === ModelType.USER &&
          log?.category === LogCategory.EMAIL_SENT &&
          log?.source !== "Oz"
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : [];

  const userStats = [
    { label: 'Last Login', value: formatDate(user?.lastLogin) },
    { label: 'Last Activity', value: formatDate(user?.lastActivity) },
    { label: 'Created', value: formatDate(user?.createdAt) },
    { label: 'Emails Received', value: emailLogs.length },
  ];

  // TEMP DIAGNOSTIC — every log for this user regardless of category/source,
  // to compare actual field values against the emailLogs filter above.
  if (Array.isArray(logs)) {
    const userScopedLogs = logs.filter(
      (log) => log?.entityId === user?.id && log?.modelType === ModelType.USER
    );
    console.table(
      userScopedLogs.map((log) => ({
        id: log.id,
        source: log.source,
        category: log.category,
        type: log.type,
        createdAt: log.createdAt,
      }))
    );
  }

  const loginCount = Array.isArray(user?.logins) ? user.logins.length : 0;

  const sections = [
    { id: 'login', label: 'Login History', count: loginCount },
    { id: 'event', label: 'Event Log', count: userLogs.length },
    { id: 'email', label: 'Email Log', count: emailLogs.length },
  ];

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const metaFields = [
    { key: 'email', label: 'Email', icon: MailOutlineIcon, color: '#3987e5', span: 'full', tint: 'blue', inline: true },
    { key: 'givenName', label: 'First Name', icon: PersonOutlineIcon, color: '#2e9e6b', span: 'half', tint: 'teal' },
    { key: 'familyName', label: 'Last Name', icon: PersonOutlineIcon, color: '#2e9e6b', span: 'half', tint: 'teal' },
  ];

  // logData.message carries the human-readable summary on some COMPLETE rows
  // (e.g. Oz's wrapper event, whose own description is just "Event complete -
  // Unknown event"); description is the better text everywhere else.
  const getLogText = (log) => log?.logData?.message || log?.description || '';

  const renderEventLogTable = () => (
    <table className="ufp-log-table">
      <thead>
        <tr>
          <th>Time</th>
          <th>Category</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {userLogs.map((log) => (
          <tr key={log.id}>
            <td>{formatDate(log.createdAt)}</td>
            <td title={log.category}>{log.category}</td>
            <td title={getLogText(log)}>{getLogText(log)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderEventLogFeed = () => (
    <div className="ufp-log-feed">
      {userLogs.map((log, i) => {
        const side = i % 2 === 0 ? 'left' : 'right';
        const post = (
          <div className="ufp-log-feed-post">
            <div className="ufp-log-feed-header">
              <strong>{log.category?.replace(/_/g, ' ')}</strong>
              <span className="ufp-log-feed-time">{formatDate(log.createdAt)}</span>
            </div>
            {getLogText(log) && (
              <div className="ufp-log-feed-desc" title={getLogText(log)}>{getLogText(log)}</div>
            )}
          </div>
        );
        return (
          <div className={`ufp-log-feed-item ufp-log-feed-item--${side}`} key={log.id}>
            {side === 'left' && post}
            <span className="ufp-log-feed-dot" />
            {side === 'right' && post}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="card-columns-with-gap">
      {/* PANEL A */}
      <div className="resizable-left panel-card ufp-panel-a">

<div
  className={`ufp-panel-a-blur${speedDialOpen ? ' active' : ''}`}
  onClick={() => setSpeedDialOpen(false)}
/>

<SpeedDial
  ariaLabel="User Actions"
  icon={<EditIcon />}
  direction="down"
  className="ufp-speed-dial"
  open={speedDialOpen}
  onOpen={() => setSpeedDialOpen(true)}
  onClose={() => setSpeedDialOpen(false)}
>
  <SpeedDialAction icon={<LockResetIcon />} tooltipTitle="Reset Password" onClick={handleResetPasswordClick} />
  <SpeedDialAction icon={<MailOutlineIcon />} tooltipTitle="Send Password Reset Email" onClick={runAction(onSendPasswordResetEmail, 'Send Password Reset Email')} />
  <SpeedDialAction icon={<PersonOffIcon />} tooltipTitle="Deactivate" onClick={runAction(onDeactivate, 'Deactivate')} />
  <SpeedDialAction icon={<AdminPanelSettingsIcon />} tooltipTitle="Adminise" onClick={runAction(onAdminise, 'Adminise')} />
</SpeedDial>
        <div className="data-card-header">
          <div className="ufp-header-name">{formData.givenName} {formData.familyName}</div>
          {roleBadges.length > 0 && (
            <div className="ufp-header-roles">
              {roleBadges.map(({ key, label, icon: Icon }) => (
                <Tooltip title={label} key={key}>
                  <Icon fontSize="small" className="ufp-header-role-icon" />
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        <div className="ufp-summary-cards">
          {summaryCards.map((card) => (
            <div className="ufp-summary-card" key={card.key}>
              <div className="ufp-summary-card-value">{card.value}</div>
              <div className="ufp-summary-card-label">{card.label}</div>
              <div className="ufp-summary-card-breakdown">
                {card.breakdown.map((b) => (
                  <div className="ufp-summary-card-stat" key={b.label}>
                    <span className="ufp-summary-card-stat-value">{b.value}</span>
                    <span className="ufp-summary-card-stat-label">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="ufp-stats-grid">
          {userStats.map(({ label, value }) => (
            <div key={label} className="ufp-stat-block">
              <div className="ufp-stat-label">{label}</div>
              <div className="ufp-stat-value">{value}</div>
            </div>
          ))}
        </div>

        <div className="ufp-meta-fields">
          {metaFields.map(({ key, label, icon: Icon, color, span, tint, inline }) => (
            <div className={`ufp-meta-card ufp-meta-card--${span} ufp-meta-card--${tint}`} key={key}>
              <Icon className="ufp-meta-card-icon" style={{ color }} />
              <div className={`ufp-meta-card-body${inline ? ' ufp-meta-card-body--inline' : ''}`}>
                <label className="ufp-label">{label}</label>
                <TextField
                  value={formData[key] || ''}
                  onChange={handleChange(key)}
                  onFocus={() => setFocusedField(key)}
                  onBlur={() => setFocusedField(null)}
                  className={`ufp-input ${focusedField === key ? 'editing' : 'readonly'}`}
                  variant="standard"
                  fullWidth
                />
              </div>
            </div>
          ))}

          <div className="ufp-meta-card ufp-meta-card--full ufp-meta-card--orange">
            <BadgeIcon className="ufp-meta-card-icon" style={{ color: '#ea602e' }} />
            <div className="ufp-meta-card-body">
              <label className="ufp-label">User ID</label>
              <TextField
                value={formData.id}
                InputProps={{ readOnly: true }}
                className="ufp-input readonly"
                variant="standard"
                fullWidth
              />
            </div>
          </div>
        </div>

        <Dialog
          open={showPasswordForm}
          onClose={() => setShowPasswordForm(false)}
          PaperProps={{
            style: {
              backgroundColor: '#000',
              border: '2px solid rgba(255, 255, 255, 0.24)',
              borderRadius: '14px',
              padding: '10px',
              width: '600px',
            },
          }}
        >
          <SetPasswordFields
            title="Set a new password"
            onClose={() => setShowPasswordForm(false)}
            onSetPassword={(password) => {
              onResetPassword(user, password);
              setShowPasswordForm(false);
            }}
          />
        </Dialog>
      </div>

      {/* PANEL B */}
      <div className="resizable-right panel-card ufp-panel-b">
        <div className="meta-card-content meta-card-content--grow">
          <div className="ufp-panel-body">
            <div className="ufp-tabs">
              <span className="ufp-logs-label">Logs</span>
              {sections.map((section) => (
                <div
                  key={section.id}
                  className={`ufp-tab${activeSection === section.id ? ' active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span>{section.label}</span>
                  <span className="ufp-tab-count">{section.count}</span>
                </div>
              ))}

              {activeSection === 'event' && (
                <div className="ufp-log-view-toggle">
                  <Tooltip title="Table view">
                    <IconButton
                      size="small"
                      onClick={() => setEventLogView('table')}
                      className={eventLogView === 'table' ? 'active' : ''}
                    >
                      <ViewListIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Feed view">
                    <IconButton
                      size="small"
                      onClick={() => setEventLogView('feed')}
                      className={eventLogView === 'feed' ? 'active' : ''}
                    >
                      <DynamicFeedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>

            <div className="ufp-tab-content">
              {activeSection === 'login' && (
                <LoginHistoryTimeline logins={user?.logins} />
              )}

              {activeSection === 'event' && (
                <div className="ufp-logs">
                  {userLogs.length > 0 ? (
                    eventLogView === 'table' ? renderEventLogTable() : renderEventLogFeed()
                  ) : (
                    'No events recorded'
                  )}
                </div>
              )}

              {activeSection === 'email' && (
                <div className="ufp-logs">
                  {emailLogs.length > 0 ? (
                    emailLogs.map((log) => (
                      <div key={log.id} className="ufp-email-row" title={getLogText(log)}>
                        {formatDate(log.createdAt)} — {getLogText(log)}
                      </div>
                    ))
                  ) : (
                    'No emails recorded'
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
